/**
 * Main Intelligence Dashboard Page (v3.1)
 * Tabs: Sectors (with Telegram metrics) | Opportunities | Alerts
 */

import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { intelligenceService } from '../../services/intelligence.service'
import {
  getStageColor,
  getStageLabel,
  getEntryTimingColor,
  getEntryTimingLabel,
  getPriorityColor,
  formatLargeNumber,
  formatPercentChange,
  type CryptoSector,
  type SectorSignal,
} from '../../types/intelligence'

type Tab = 'sectors' | 'opportunities' | 'alerts'

export default function IntelligencePage() {
  const [activeTab, setActiveTab] = useState<Tab>('sectors')
  const queryClient = useQueryClient()

  const { data: integrationStatus, isLoading: loadingStatus } = useQuery({
    queryKey: ['integrationStatus'],
    queryFn: () => intelligenceService.getIntegrationStatus(),
  })

  const { data: sectors, isLoading: loadingSectors } = useQuery({
    queryKey: ['sectors'],
    queryFn: () => intelligenceService.getSectors({ active_only: true, limit: 50 }),
    enabled: integrationStatus?.feature_enabled,
  })

  const { data: opportunities, isLoading: loadingOpportunities } = useQuery({
    queryKey: ['sectorOpportunities'],
    queryFn: () => intelligenceService.getSectorOpportunities({ min_score: 50, limit: 20 }),
    enabled: integrationStatus?.feature_enabled && activeTab === 'opportunities',
  })

  const { data: alerts, isLoading: loadingAlerts } = useQuery({
    queryKey: ['marketAlerts'],
    queryFn: () => intelligenceService.getAlerts({ unread_only: false, limit: 20 }),
    enabled: integrationStatus?.feature_enabled && activeTab === 'alerts',
  })

  const markReadMutation = useMutation({
    mutationFn: (alertId: number) => intelligenceService.markAlertRead(alertId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['marketAlerts'] }),
  })

  if (loadingStatus) {
    return (
      <div className="p-6">
        <div className="card text-center py-12">
          <div className="animate-spin w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-gray-500">Загрузка...</p>
        </div>
      </div>
    )
  }

  if (!integrationStatus?.feature_enabled) {
    return (
      <div className="p-6">
        <div className="card text-center py-12">
          <div className="text-6xl mb-4">🔒</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Crypto Intelligence (v3.1)</h2>
          <p className="text-gray-500">
            Функция находится в разработке и будет доступна в ближайшее время.
          </p>
        </div>
      </div>
    )
  }

  const tabs: { id: Tab; label: string; icon: string }[] = [
    { id: 'sectors', label: 'Секторы', icon: '📡' },
    { id: 'opportunities', label: 'Возможности', icon: '🔥' },
    { id: 'alerts', label: 'Алерты', icon: '🚨' },
  ]

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Market Intelligence</h1>
          <p className="text-gray-500">Crypto sector tracking & signals</p>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <span className={`flex items-center gap-1 ${integrationStatus.dune_configured ? 'text-green-600' : 'text-gray-400'}`}>
            <span className="w-2 h-2 rounded-full bg-current" />
            Dune
          </span>
          <span className={`flex items-center gap-1 ${integrationStatus.x_configured ? 'text-green-600' : 'text-gray-400'}`}>
            <span className="w-2 h-2 rounded-full bg-current" />
            X/Twitter
          </span>
          <span className={`flex items-center gap-1 ${integrationStatus.telegram_configured ? 'text-green-600' : 'text-gray-400'}`}>
            <span className="w-2 h-2 rounded-full bg-current" />
            Telegram
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Sectors Tab */}
      {activeTab === 'sectors' && (
        <SectorsTab sectors={sectors} isLoading={loadingSectors} />
      )}

      {/* Opportunities Tab */}
      {activeTab === 'opportunities' && (
        <OpportunitiesTab opportunities={opportunities} isLoading={loadingOpportunities} />
      )}

      {/* Alerts Tab */}
      {activeTab === 'alerts' && (
        <AlertsTab
          alerts={alerts}
          isLoading={loadingAlerts}
          onMarkRead={(id) => markReadMutation.mutate(id)}
        />
      )}
    </div>
  )
}

// ============================================================
// SECTORS TAB
// ============================================================

function SectorsTab({
  sectors,
  isLoading,
}: {
  sectors: CryptoSector[] | undefined
  isLoading: boolean
}) {
  if (isLoading) return <TabSkeleton rows={6} />

  if (!sectors?.length) {
    return (
      <div className="card text-center py-12">
        <div className="text-4xl mb-4">📡</div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Нет активных секторов</h3>
        <p className="text-gray-500 text-sm">
          Добавьте секторы через admin панель и запустите первое сканирование.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">
        {sectors.length} активных сектора · Данные обновляются ежедневно в 06:00 UTC
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {sectors.map((sector) => (
          <SectorCard key={sector.id} sector={sector} />
        ))}
      </div>
    </div>
  )
}

function SectorCard({ sector }: { sector: CryptoSector }) {
  const { data: signals } = useQuery({
    queryKey: ['sectorSignals', sector.id],
    queryFn: () => intelligenceService.getSectorSignals(sector.id, 1),
    staleTime: 5 * 60 * 1000,
  })

  const latest: SectorSignal | undefined = signals?.[0]

  return (
    <div className="card hover:shadow-md transition-shadow">
      {/* Title row */}
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="font-semibold text-gray-900">{sector.name}</h3>
          {sector.description && (
            <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{sector.description}</p>
          )}
        </div>
        <span className={`px-2 py-0.5 text-xs rounded flex-shrink-0 ml-2 ${getStageColor(sector.stage)}`}>
          {getStageLabel(sector.stage)}
        </span>
      </div>

      {/* Telegram metrics */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        <MetricCell
          label="Каналов"
          value={latest?.telegram_channels_count?.toLocaleString() ?? '—'}
          sub={
            latest?.telegram_new_channels_7d != null
              ? `+${latest.telegram_new_channels_7d} за 7д`
              : undefined
          }
          subColor="text-green-600"
          icon="📡"
        />
        <MetricCell
          label="Score"
          value={latest?.opportunity_score != null ? `${latest.opportunity_score}/100` : '—'}
          icon="🎯"
        />
      </div>

      {/* Keywords */}
      {sector.keywords?.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {sector.keywords.slice(0, 4).map((kw) => (
            <span key={kw} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
              {kw}
            </span>
          ))}
          {sector.keywords.length > 4 && (
            <span className="text-xs text-gray-400">+{sector.keywords.length - 4}</span>
          )}
        </div>
      )}

      {/* Last updated */}
      {latest && (
        <p className="text-xs text-gray-400 mt-3">
          Обновлено: {new Date(latest.date).toLocaleDateString('ru-RU')}
        </p>
      )}
    </div>
  )
}

function MetricCell({
  label,
  value,
  sub,
  subColor = 'text-gray-500',
  icon,
}: {
  label: string
  value: string
  sub?: string
  subColor?: string
  icon: string
}) {
  return (
    <div className="bg-gray-50 rounded-lg p-2">
      <div className="text-xs text-gray-500 mb-1">
        {icon} {label}
      </div>
      <div className="font-semibold text-gray-900">{value}</div>
      {sub && <div className={`text-xs ${subColor}`}>{sub}</div>}
    </div>
  )
}

// ============================================================
// OPPORTUNITIES TAB
// ============================================================

function OpportunitiesTab({
  opportunities,
  isLoading,
}: {
  opportunities: any[] | undefined
  isLoading: boolean
}) {
  if (isLoading) return <TabSkeleton rows={4} />

  if (!opportunities?.length) {
    return (
      <div className="card text-center py-12">
        <div className="text-4xl mb-4">🔥</div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Нет активных возможностей</h3>
        <p className="text-gray-500 text-sm">
          Данные появятся после настройки Dune Analytics и X/Twitter (v3.2–3.3).
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {opportunities.map((opp) => (
        <div
          key={opp.sector.id}
          className="card hover:border-blue-300 border border-gray-200 transition-colors"
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="font-semibold text-gray-900">{opp.sector.name}</h3>
                <span className={`px-2 py-0.5 text-xs rounded ${getStageColor(opp.sector.stage)}`}>
                  {getStageLabel(opp.sector.stage)}
                </span>
              </div>

              {opp.latest_signal && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div>
                    <div className="text-gray-500 text-xs">Score</div>
                    <div className="font-semibold text-lg">
                      {opp.latest_signal.opportunity_score ?? '—'}/100
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-500 text-xs">Volume</div>
                    <div className="font-medium">
                      {formatLargeNumber(opp.latest_signal.dune_volume_usd)}
                    </div>
                    <div className="text-xs text-green-600">
                      {formatPercentChange(opp.latest_signal.dune_volume_change_pct)}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-500 text-xs">Каналы TG</div>
                    <div className="font-medium">
                      {opp.latest_signal.telegram_channels_count?.toLocaleString() ?? '—'}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-500 text-xs">X Mentions</div>
                    <div className="font-medium">
                      {opp.latest_signal.x_mentions_count?.toLocaleString() ?? '—'}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <span
              className={`ml-4 px-3 py-1 text-sm font-medium rounded-full flex-shrink-0 ${getEntryTimingColor(opp.entry_timing)}`}
            >
              {getEntryTimingLabel(opp.entry_timing)}
            </span>
          </div>
        </div>
      ))}
    </div>
  )
}

// ============================================================
// ALERTS TAB
// ============================================================

function AlertsTab({
  alerts,
  isLoading,
  onMarkRead,
}: {
  alerts: any[] | undefined
  isLoading: boolean
  onMarkRead: (id: number) => void
}) {
  if (isLoading) return <TabSkeleton rows={5} />

  if (!alerts?.length) {
    return (
      <div className="card text-center py-12">
        <div className="text-4xl mb-4">✅</div>
        <h3 className="text-lg font-medium text-gray-900">Нет уведомлений</h3>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {alerts.map((alert) => (
        <div
          key={alert.id}
          className={`card border ${getPriorityColor(alert.priority)} ${
            !alert.is_read ? 'ring-1 ring-current' : 'opacity-75'
          }`}
        >
          <div className="flex items-start justify-between">
            <div>
              <div className="font-medium text-sm">{alert.title}</div>
              {alert.description && (
                <p className="text-xs mt-1 opacity-80">{alert.description}</p>
              )}
              {alert.sector_name && (
                <div className="text-xs mt-1 opacity-60">#{alert.sector_name}</div>
              )}
            </div>
            {!alert.is_read && (
              <button
                onClick={() => onMarkRead(alert.id)}
                className="ml-3 text-xs text-gray-500 hover:text-gray-700 flex-shrink-0"
              >
                Прочитано
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

// ============================================================
// HELPERS
// ============================================================

function TabSkeleton({ rows }: { rows: number }) {
  return (
    <div className="animate-pulse space-y-4">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="h-24 bg-gray-100 rounded-lg" />
      ))}
    </div>
  )
}
